import { ELEMENTS, type Element, type Locale } from '../constants'
import { elementCounts, type ChartPillars } from '../chartFacts'
import { hanjaSystem, hanjaTableElement } from './hanjaTable'
import { elementBalance } from '../elementBalance'

/**
 * 이름 풀이 — **소리 오행**.
 *
 * 예전에는 한글 자모의 획수로 봤는데, 한자를 넣지 않아도 획수가 나와서
 * 근거 없는 숫자처럼 보였다 (사용자 제보). 이제 **이름을 소리 낼 때 나는 기운**으로 본다.
 *
 *   ㄱ ㅋ        나무   어금닛소리
 *   ㄴ ㄷ ㄹ ㅌ   불     혓소리
 *   ㅇ ㅎ        흙     목구멍소리
 *   ㅅ ㅈ ㅊ     쇠     잇소리
 *   ㅁ ㅂ ㅍ     물     입술소리
 *
 * 알파벳도 같은 소리 계열로 잇는다. 그래서 한글이 아닌 이름도 돌아간다.
 *
 * 아이 이름을 지을 때 참고하는 방식이지, 이름이 운을 정한다는 뜻이 아니다.
 */

/** 한글 초성 19개의 소리 오행 (ㄱㄲㄴㄷㄸㄹㅁㅂㅃㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ) */
const CHO_ELEMENTS: Element[] = [
  'wood', // ㄱ
  'wood', // ㄲ
  'fire', // ㄴ
  'fire', // ㄷ
  'fire', // ㄸ
  'fire', // ㄹ
  'water', // ㅁ
  'water', // ㅂ
  'water', // ㅃ
  'metal', // ㅅ
  'metal', // ㅆ
  'earth', // ㅇ
  'metal', // ㅈ
  'metal', // ㅉ
  'metal', // ㅊ
  'wood', // ㅋ
  'fire', // ㅌ
  'water', // ㅍ
  'earth', // ㅎ
]

/** 알파벳 첫소리의 오행. 두 글자 소리(ch, sh…)를 먼저 본다. */
const LATIN_PAIRS: [string, Element][] = [
  ['ch', 'metal'],
  ['sh', 'metal'],
  ['ts', 'metal'],
  ['th', 'fire'],
  ['ph', 'water'],
  ['kh', 'wood'],
  ['gh', 'wood'],
]

const LATIN_SINGLE: Record<string, Element> = {
  g: 'wood',
  k: 'wood',
  q: 'wood',
  n: 'fire',
  d: 'fire',
  l: 'fire',
  t: 'fire',
  r: 'fire',
  a: 'earth',
  e: 'earth',
  i: 'earth',
  o: 'earth',
  u: 'earth',
  y: 'earth',
  h: 'earth',
  w: 'earth',
  s: 'metal',
  j: 'metal',
  c: 'metal',
  z: 'metal',
  x: 'metal',
  m: 'water',
  b: 'water',
  p: 'water',
  f: 'water',
  v: 'water',
}

/**
 * 가나 첫소리의 오행. 일본어 이름을 그 나라 소리로 읽기 위해서다.
 * 행(行) 단위로 본다 — あ행은 목구멍소리(흙), か행은 어금닛소리(나무)… 하는 식.
 */
const KANA_ROWS: [RegExp, Element][] = [
  // か行 — 어금닛소리
  [/[かがきぎくぐけげこごカガキギクグケゲコゴ]/, 'wood'],
  // た·な·ら행 — 혓소리
  [/[ただちぢつづてでとどタダチヂツヅテデトドなにぬねのナニヌネノらりるれろラリルレロ]/, 'fire'],
  // あ·や·わ·は행 — 목구멍소리
  [/[あいうえおアイウエオやゆよヤユヨわをんワヲンはひふへほハヒフヘホ]/, 'earth'],
  // さ행 — 잇소리
  [/[さざしじすずせぜそぞサザシジスズセゼソゾ]/, 'metal'],
  // ま·ば·ぱ행 — 입술소리
  [/[まみむめもマミムメモばびぶべぼバビブベボぱぴぷぺぽパピプペポ]/, 'water'],
]

/**
 * 한자의 첫소리 오행.
 *
 * 한자는 글자만으로 소리를 알 수 없어, 자주 쓰는 글자만 손으로 적어 둔 표다.
 *
 * **아직 251자뿐이다.** 성씨와 흔한 이름 글자는 한국 한자음 기준으로 넣었지만
 * (金=김→ㄱ→나무), 인명용 한자 전체를 덮지 못한다 — 鄭在桓 에서 在·桓 이 빠져
 * "읽지 못함" 이 된다 (실측). 언어마다 읽는 법이 다른 것(한국 한자음 / 일본 음독 /
 * 병음)도 아직 갈라 두지 않았다.
 *
 * 제대로 하려면 공개 한자음 자료(Unicode Unihan 의 kHangul·kJapaneseOn·kMandarin)를
 * 넣어야 한다. 바깥 자료를 받아 넣는 일이라 사용자 승인을 받고 한다 (docs/TODO_LEGAL.md).
 *
 * 없는 글자는 그대로 "읽지 못함" 으로 두고 화면이 안내한다 — 잘못 읽는 것보다 낫다.
 * 성모 → 오행은 한글 초성과 같은 자리로 잇는다:
 *   g/k/q → 나무, n/d/l/t/r → 불, 모음·h/y/w → 흙, s/z/c/j/x/zh/ch/sh → 쇠, m/b/p/f → 물
 */
const HANJA_SOUND: Record<string, Element> = {}

function addHanja(element: Element, chars: string): void {
  for (const ch of chars) HANJA_SOUND[ch] = element
}

// 나무 — g, k, q 계열
addHanja('wood', '金高郭具權琴康強桂姜介健京慶吉光國君貴龜乾坤')
addHanja('wood', '谷公孔郡群琦起錡格結京鏡見官冠寬廣卿')
// 불 — n, d, l, t, r 계열
addHanja('fire', '羅李林南盧魯柳劉呂廉雷龍陸樂唐段大德道東童杜')
addHanja('fire', '蘭來良麗蓮露魯論丹端達談')
// 흙 — 모음, h, y, w 계열
addHanja('earth', '安吳王元尹柳兪禹嚴呂阿愛英榮永遠園源恩銀義伊人仁')
addHanja('earth', '韓河洪黃許玄惠慧和花華厚孝喜熙希賢賀海行幸')
addHanja('earth', '衛魏溫雲雨宇友右于云艶燕彦雅野陽楊揚已以怡')
// 쇠 — s, z, c, j, x, zh, ch, sh 계열
addHanja('metal', '徐宋申沈孫成蘇石薛晨承昇時是詩新信善誠世昭少小')
addHanja('metal', '張趙鄭朱周曾鍾鐘智之志眞珍振正貞靜宗')
addHanja('metal', '崔蔡車秦陳程陶春秋靑淸淳純子字慈')
addHanja('metal', '謝蕭徐薛須修秀壽守洙璇善宣先仙雪')
addHanja('metal', '謝夏小蕭孝心新')
// 물 — m, b, p, f 계열
addHanja('water', '文明閔朴白潘方房裵范馮傅馬牟穆美敏旼民茂武')
addHanja('water', '培保寶福鳳鵬彬斌炳丙秉本')
addHanja('water', '彭平浦表豊馮夫富扶芬粉')

/** 한 글자(또는 한 음절)가 내는 소리의 오행. 읽을 수 없으면 null */
export function soundElement(ch: string): Element | null {
  const code = ch.codePointAt(0)
  if (code === undefined) return null

  // 한글 음절 → 초성
  if (code >= 0xac00 && code <= 0xd7a3) {
    return CHO_ELEMENTS[Math.floor((code - 0xac00) / (21 * 28))] ?? null
  }
  /*
   * 조합용 초성 (U+1100~U+1112). NFC 로 합치지 못하고 남은 낱자가 있을 때를 위한 그물이다.
   * 차례가 호환 자모(ㄱ~ㅎ)와 달라 따로 잇는다.
   */
  if (code >= 0x1100 && code <= 0x1112) {
    return CHO_ELEMENTS[code - 0x1100] ?? null
  }
  // 홀로 쓴 한글 자음 (ㄱ~ㅎ)
  if (code >= 0x3131 && code <= 0x314e) {
    const JAMO = 'ㄱㄲㄳㄴㄵㄶㄷㄸㄹㄺㄻㄼㄽㄾㄿㅀㅁㅂㅃㅄㅅㅆㅇㅈㅉㅊㅋㅌㅍㅎ'
    const MAP: Record<string, Element> = {
      ㄱ: 'wood', ㄲ: 'wood', ㅋ: 'wood',
      ㄴ: 'fire', ㄷ: 'fire', ㄸ: 'fire', ㄹ: 'fire', ㅌ: 'fire',
      ㅇ: 'earth', ㅎ: 'earth',
      ㅅ: 'metal', ㅆ: 'metal', ㅈ: 'metal', ㅉ: 'metal', ㅊ: 'metal',
      ㅁ: 'water', ㅂ: 'water', ㅃ: 'water', ㅍ: 'water',
    }
    return MAP[JAMO[code - 0x3131] ?? ''] ?? null
  }
  // 가나 — 첫 글자로 본다 (「ショ」「りょ」 처럼 작은 글자를 붙인 덩어리도 앞 글자의 행)
  const head = String.fromCodePoint(code)
  for (const [re, element] of KANA_ROWS) if (re.test(head)) return element
  // 한자 (자주 쓰는 글자만)
  return HANJA_SOUND[ch] ?? null
}

/** 알파벳 낱말 하나의 첫소리 오행 */
/**
 * **그 사주의 언어로 읽은 첫소리.** 예전에는 언어와 상관없이 g→나무·c→쇠·j→쇠로 정해
 * es "Gerardo"(Ge = ㅎ)·pt "Giovana"(Gi = ㅈ)·pt "Caio"(Ca = ㅋ)·en "George"(Ge = ㅈ)의 기운이 틀렸다.
 *
 *          Ge·Gi·J      Ca·Co·Cu·Cl·Cr·Qu·K·Q   Ce·Ci(·Ç)·S·Z
 *   es     ㅎ 흙         ㄱ 나무                  ㅅ 쇠
 *   pt     ㅈ 쇠         ㄱ 나무                  ㅅ 쇠
 *   en     ㅈ 쇠         ㄱ 나무(+ Chr·Chl)       ㅅ 쇠      (Ga·Go·Gu 는 그대로 ㄱ)
 * 그 밖 언어·글자는 예전 표 그대로다. 화면의 기운 표시·"이미 이 소리가 있다"·별명 보기 고르기가 모두 이 함수를 쓴다.
 */
export function latinElement(word: string, locale?: Locale): Element | null {
  const raw = word.normalize('NFC').toLowerCase()
  const lower = foldAccents(word).toLowerCase()
  if (locale === 'zh-Hans' || locale === 'zh-Hant') {
    // 병음 성모와 번체권의 威妥瑪 표기. q 는 영어식 k 소리가 아니다.
    if (/^(zh|ch|sh|hs|ts|tz|j|q|x|z|c|s)/.test(lower)) return 'metal'
    if (/^(y|w|h|[aeiou])/.test(lower)) return 'earth'
    if (/^(g|k)/.test(lower)) return 'wood'
    if (/^(n|d|l|t|r)/.test(lower)) return 'fire'
    if (/^(m|b|p|f)/.test(lower)) return 'water'
  }
  if (locale === 'es' || locale === 'pt-BR' || locale === 'en') {
    // 브라질 이름의 첫 R (Rafael, Ricardo)은 [h]에 가까워 H와 같은 갈래다.
    if (locale === 'pt-BR' && lower.startsWith('r')) return 'earth'
    if (raw.startsWith('ç')) return 'metal'
    if (locale === 'es' && lower.startsWith('ll')) return 'fire'
    if (locale === 'pt-BR' && /^(lh|nh)/.test(lower)) return 'fire'
    if (!lower.startsWith('ch') && /^c[eiy]/.test(lower)) return 'metal'
    if (/^(c[aoulr]|qu|k|q)/.test(lower)) return 'wood'
    // en Chr·Chl 은 ㅋ 소리 (Chris·Chloe). 그 밖 Ch 는 예전 표대로 쇠
    if (locale === 'en' && /^ch[rl]/.test(lower)) return 'wood'
    if (/^(g[eiy]|j)/.test(lower)) return locale === 'es' ? 'earth' : 'metal'
  }
  for (const [pair, element] of LATIN_PAIRS) if (lower.startsWith(pair)) return element
  return LATIN_SINGLE[lower[0] ?? ''] ?? null
}

export interface NameUnit {
  /** 화면에 보여 줄 글자 (한글 한 음절, 알파벳은 낱말) */
  text: string
  element: Element | null
}

/**
 * 이웃한 두 글자의 관계. **방향까지 가른다**.
 *
 * 예전에는 `helps`·`clashes` 둘로만 보아, 어느 쪽이 어느 쪽을 낳고 누르는지 잃었다.
 * 그래서 **쇠→흙** 에 "앞 글자가 뒤 글자를 밀어 주는 사이" 가 붙었다 — 상생은
 * 흙→쇠 방향이니 **뒤 글자가 앞 글자를 받쳐 주는** 사이가 맞다 (실측: 이지연 '지→연',
 * 정상훈 '상→훈', 정성원 '성→원' 세 자리가 전부 거꾸로 적혀 있었다).
 *
 *   same          같은 기운
 *   feedsNext     앞 글자가 뒤 글자를 낳는다 (앞 → 뒤 상생)
 *   feedsPrev     뒤 글자가 앞 글자를 낳는다 (뒤 → 앞 상생)
 *   pressesNext   앞 글자가 뒤 글자를 누른다 (앞 → 뒤 상극)
 *   pressesPrev   뒤 글자가 앞 글자를 누른다 (뒤 → 앞 상극)
 *   plain         아무 관계도 아니다 — 오행 다섯끼리는 실제로 생기지 않는다 (막아 두는 값)
 */
export type Bond = 'same' | 'feedsNext' | 'feedsPrev' | 'pressesNext' | 'pressesPrev' | 'plain'

/** 상생 쪽인가 (방향 무관) */
export function isFeed(bond: Bond): boolean {
  return bond === 'feedsNext' || bond === 'feedsPrev'
}
/** 상극 쪽인가 (방향 무관) */
export function isPress(bond: Bond): boolean {
  return bond === 'pressesNext' || bond === 'pressesPrev'
}

export interface NamePair {
  from: string
  to: string
  bond: Bond
}

export interface NameReading {
  units: NameUnit[]
  /** 이름이 담은 오행 (중복 없이) */
  nameElements: Element[]
  /** 오행별 글자 수 */
  counts: Record<Element, number>
  /** 사주 오행 수 */
  chartCounts: Record<Element, number>
  /** 사주에서 모자란 오행 */
  needed: Element
  /** 사주에서 가장 많은 오행 */
  strongest: Element
  /** 이름이 모자란 기운을 담고 있는가 */
  covers: boolean
  /** 이미 넘치는 기운을 더 보태는가 — `pileList` 의 첫 기운 */
  piles: Element | null
  /** 이름이 채우는 기운 모두 (사주에서 가장 적은 기운 — 동점 포함 — 가운데 이름이 가진 것) */
  coverList: Element[]
  /** 이름이 더 보태는 기운 모두 (이름을 더한 뒤 가장 많아진 기운 가운데 이름이 보탠 것, 채운 기운 빼고) */
  pileList: Element[]
  /** 이웃한 글자끼리의 관계 */
  pairs: NamePair[]
  /** 소리를 읽지 못한 글자 수 */
  unreadable: number
  /** 별명·영어 이름에 쓰면 좋은 첫소리 */
  suggest: Element
  /**
   * 이름에서 **가장 센 소리**. 같은 수면 이름에서 먼저 나오는 쪽.
   * 읽은 글자가 하나도 없으면 null.
   *
   * 예전에는 화면이 오행 **집합**(`nameElements`)만 보고 글을 찍어서,
   * 이지연과 정성원처럼 집합이 같은 이름은 글자 그대로 같은 결과가 나왔다 (제보).
   * 개수를 세면 이지연은 흙, 정성원은 쇠로 갈린다.
   */
  dominant: Element | null
  /**
   * **가장 센 것이 여럿과 같은 수인가** — 이름 소리(`soundTied`)·사주(`chartTied`). 같은 수면
   * "○○가 많다/세다" 고 하지 않고 "고르게 있다" 고 쓴다. `dominant`·`strongest` 값은 그대로 둔다 (저장 캐시 열쇠가 쓴다).
   */
  soundTied: Element[]
  chartTied: Element[]
  /** 읽은 글자 수 (전체 글자 수는 `units.length`) */
  readable: number
  /** 사주 + 이름을 합친 오행 수. 이름마다 달라진다. */
  combined: Record<Element, number>
  /** 모자란 기운을 이름의 몇 글자가 채우는가 */
  fillsWith: number
  /** 글자끼리의 사이를 한마디로 (방향은 지운 값 — 갈래만 본다) */
  bondMood: 'feeds' | 'presses' | 'same' | 'plain' | 'mixed' | 'none'

  /* ------ 아래는 **뒷글을 갈라 쓰려고** 보는 값들 ------
     예전에는 뒷글이 한 틀에 빈칸만 갈아 끼워, 누가 넣어도 같은 글로 읽혔다. */

  /** 맨 끝에 남는 소리. 읽은 글자가 없으면 null */
  lastElement: Element | null
  /** 첫소리와 끝소리가 같은가 */
  endsMatch: boolean
  /**
   * 소리가 어떻게 흐르는가 — 뒷글을 가르는 네 갈래.
   *   feeds        내내 받쳐 준다 (상생만)
   *   feedsSame    받쳐 주는 자리 + 같은 기운 (누르는 자리는 없다)
   *   presses      내내 누른다 (상극만)
   *   pressesSame  누르는 자리 + 같은 기운 (받쳐 주는 자리는 없다)
   *   steady       같은 기운만 이어진다
   *   mixed        받쳐 주는 자리와 누르는 자리가 **함께** 있다
   *   none         견줄 이웃이 없다 (읽은 글자가 하나뿐)
   */
  flow: 'feeds' | 'feedsSame' | 'presses' | 'pressesSame' | 'steady' | 'mixed' | 'none'
  /**
   * 같은 소리가 얼마나 겹치는가.
   *   all   읽은 글자가 **전부** 한 기운 (두 자 이상일 때만)
   *   some  어떤 기운이 두 번 이상
   *   none  다 다르다
   */
  repeat: 'all' | 'some' | 'none'
  /** 가장 많이 겹친 기운과 그 수 */
  repeatElement: Element | null
  repeatCount: number
  /** 읽은 글자 수로 본 길이. short 1자 · normal 2~3자 · long 4자 이상 */
  length: 'short' | 'normal' | 'long'
  /**
   * 사주와의 관계 — **네 갈래**. 예전에는 `covers`·`piles` 둘만 보아
   * "둘 다" 와 "둘 다 아님" 이 같은 틀로 나왔다.
   */
  fit: 'covers' | 'piles' | 'both' | 'none'

  /* ------ 아래 둘은 **마지막 두 단락**을 가르려고 보는 값이다 ------
   *
   * `fit` 만으로는 부족했다. 사주는 한 사람의 것이라 고정이고, 열 이름을 넣어도
   * 여섯이 `piles` 한 곳에 몰렸다 (실측). 그러면 빈칸(`{letters}`)만 갈릴 뿐
   * 문장은 같은 글로 읽힌다. 「별명」 단락은 아예 틀이 하나였다 —
   * `suggest` 가 열 중 여덟에서 같은 기운이었다. */

  /**
   * 사주를 채우거나 얹는 글자가 **이름의 어디에 있는가.**
   *   spread  읽은 글자가 전부 그 소리다 (한 글자짜리 이름도 여기)
   *   first   첫 글자에 있다
   *   last    끝 글자에 있다
   *   middle  가운데에만 있다
   *   none    그런 글자가 없다 (`fit === 'none'`)
   */
  fitWhere: 'first' | 'last' | 'middle' | 'spread' | 'spreadMixed' | 'none'
  /** 채우거나 얹는 글자들 */
  fitLetters: string[]
  /** 채우는 글자와 더 보태는 글자 (글자마다의 기운을 보존한다). */
  coverLetters: { text: string; element: Element }[]
  pileLetters: { text: string; element: Element }[]
  /**
   * 별명·영어 이름을 **권할 자리인가.**
   *   enough      이름이 이미 모자란 기운을 채운다 → 굳이 바꿀 것이 없다
   *   fromStrong  첫소리가 이미 넉넉한 기운이다 → 부르는 소리부터 그쪽으로 기운다
   *   suggest     그 밖 → 채우는 첫소리를 권한다
   */
  nickAdvice: 'enough' | 'suggest' | 'fromStrong'

  /* ---- 아래 셋은 **「별명을 고른다면」 단락**을 넣은 이름에 맞추려고 본다 ----
   *
   * 예전에는 권하는 첫소리만 말했다. 그 소리는 사주의 모자란 기운에서 나오는데
   * 한 사람의 사주는 하나라, 어떤 이름을 넣어도 같은 문장이 나왔다 (제보). */

  /**
   * 권하는 기운의 소리가 **이름 안에 이미 있는가.** 있으면 어느 자리인가.
   *   none    없다
   *   first   첫 글자
   *   last    끝 글자
   *   middle  가운데
   *   spread  읽은 글자가 전부 그 소리
   */
  suggestWhere: 'first' | 'last' | 'middle' | 'spread' | 'none'
  /** 그 자리의 글자들 */
  suggestLetters: string[]
  /**
   * 지금 이름의 첫소리가 권하는 기운에서 **얼마나 먼가.**
   *   same     이미 그 기운이다 — 지금도 충분하다
   *   clashes  서로 누르는 사이다 — 가장 멀리 있는 소리로 시작한다
   *   near     그 밖 — 한 소리만 바꿔도 닿는다
   */
  suggestGap: 'same' | 'clashes' | 'near'
  /** 첫 글자의 소리 기운 (읽은 글자가 없으면 null) */
  firstElement: Element | null
}

/** 상생: 나무→불→흙→쇠→물→나무 */
const FEEDS: Record<Element, Element> = { wood: 'fire', fire: 'earth', earth: 'metal', metal: 'water', water: 'wood' }
/** 상극: 나무↔흙, 흙↔물, 물↔불, 불↔쇠, 쇠↔나무 */
const CONTROLS: Record<Element, Element> = { wood: 'earth', earth: 'water', water: 'fire', fire: 'metal', metal: 'wood' }

export function bondOf(a: Element, b: Element): Bond {
  if (a === b) return 'same'
  if (FEEDS[a] === b) return 'feedsNext'
  if (FEEDS[b] === a) return 'feedsPrev'
  if (CONTROLS[a] === b) return 'pressesNext'
  if (CONTROLS[b] === a) return 'pressesPrev'
  return 'plain'
}

/** 가장 큰 수를 둘 이상이 나눠 가졌으면 그 기운들, 아니면 빈 배열 (0 뿐이면 빈 배열) */
export function tiedTop(counts: Record<Element, number>): Element[] {
  const top = Math.max(...ELEMENTS.map((e) => counts[e]))
  if (top <= 0) return []
  const at = ELEMENTS.filter((e) => counts[e] === top)
  return at.length > 1 ? at : []
}

/**
 * 이름을 글자(한글·가나는 한 글자, 알파벳은 낱말)로 자른다.
 *
 * **먼저 NFC 로 합친다.** 맥의 입력기나 파일에서 온 글은 한글이 낱자로 풀린 꼴
 * (NFD)로 들어온다 — `정운영자` 이 여덟 낱자(ᄌ ᅥ ᆼ ᄌ ᅢ ᄒ ᅪ ᆫ)가 되어
 * 화면에 `「자·ㅇ·재·환」` 처럼 쪼개져 나왔다 (제보, 실측).
 * `for...of` 는 코드포인트 단위라 합쳐 주지 않으므로 여기서 합친다.
 */
/**
 * **악센트 붙은 라틴 글자도 알파벳 낱말의 한 글자다**. 예전에는 [A-Za-z] 만 낱말로 묶어
 * "José" 가 「Jos」 + 「é」(읽지 못함), "Sofía" 가 Sof·a 로 쪼개졌다. À~ɏ (곱하기·나누기 기호 빼고) 를 함께 묶는다.
 * 악센트가 없는 이름은 예전과 똑같이 쪼개진다 — 그래서 저장 캐시 열쇠도 그대로다.
 */
export const LATIN_LETTER = /[A-Za-z'\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F]/
export const LATIN_WORD = /^[A-Za-z'\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u024F]+$/

/** 소리를 볼 때만 악센트를 걷는다 (José → Jose). 화면에 보이는 글자는 그대로 */
export function foldAccents(word: string): string {
  return word
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ß/g, 'ss')
    .replace(/ø/g, 'o')
    .replace(/Ø/g, 'O')
    .replace(/æ/g, 'ae')
    .replace(/Æ/g, 'AE')
}

/** 앞 가나에 붙는 글자 — 작은 가나·촉음·늘임표 */
const KANA_JOINER = /^[ゃゅょぁぃぅぇぉゎャュョァィゥェォヮっッー]$/
const KANA_CHAR = /^[ぁ-ゖァ-ヺ]/
/**
 * **장음 う·ん 도 앞 소리에 붙인다** — 한자 읽기(藤 = とう)와 같은 음절 단위가 되게.
 * 예전에는 さとう 를 さ·と·う 로 따로 세어, 같은 이름을 한자(佐藤美咲 → さ·とう·み·さき)로 넣었을 때와 숫자·결론이 달랐다.
 * う(ウ)는 앞 소리가 お·う 단(こ·そ·と·ゆ·きょ …)일 때만 늘이는 소리다 (かう 의 う 는 따로 센다).
 */
const KANA_LONG_U = /^[うウ]$/
const KANA_N = /^[んン]$/
const O_U_ROW = /[おこごそぞとどのほぼぽもよろをょうくぐすずつづぬふぶぷむゆるゅオコゴソゾトドノホボポモヨロヲョウクグスズツヅヌフブプムユルュ]$/

function splitName(name: string): string[] {
  const out: string[] = []
  let latin = ''
  for (const ch of name.normalize('NFC')) {
    if (LATIN_LETTER.test(ch)) {
      latin += ch
      continue
    }
    if (latin) {
      out.push(latin)
      latin = ''
    }
    if (/\s/.test(ch)) continue
    // 낱말 사이 붙임표·가운뎃점·작은따옴표는 띄어쓰기와 같다 — "Yi-Jun" 의 「-」 가 "읽지 못한 글자" 가 되고 두 낱말 사이 한 쌍이 빠졌다
    if (/^[-‐–·'’]$/.test(ch) && latin === '' && out.length > 0 && LATIN_LETTER.test(out[out.length - 1]!.slice(-1))) continue
    /*
     * 작은 ゃゅょ(ャュョ)·ぁぃぅぇぉ 는 앞 글자와 한 소리(シ+ョ=ショ), っ(ッ)·ー 는 앞 소리를 늘리는 것 —
     * 따로 세면 「ョ」 가 "읽지 못함" 이 됐다. 앞 덩어리가 가나일 때만 붙인다. 이런 글자가 없는 이름은 예전과 똑같이 쪼개진다.
     */
    const prev = out[out.length - 1]
    // 「々」 는 앞 글자를 되풀이해 읽는다 (佐々木 → 佐·佐·木). 따로 세면 "읽지 못함" 이 되고 글자 사이 한 쌍이 빠졌다
    if (ch === '々' && prev && /\p{Script=Han}/u.test(prev)) {
      out.push(prev)
      continue
    }
    if (KANA_JOINER.test(ch) && prev && KANA_CHAR.test(prev)) {
      out[out.length - 1] = prev + ch
      continue
    }
    if (prev && KANA_CHAR.test(prev) && (KANA_N.test(ch) || (KANA_LONG_U.test(ch) && O_U_ROW.test(prev)))) {
      out[out.length - 1] = prev + ch
      continue
    }
    out.push(ch)
  }
  if (latin) out.push(latin)
  return out
}

/**
 * 이름 풀이.
 * @param name 이름이나 별명. 한글·알파벳·가나 모두 된다.
 */
/**
 * 한자를 읽는 음을 밖에서 받아 쓴다 (H).
 * 서버에서 받은 표를 넣으면 그 음의 첫소리로 오행을 잡는다 —
 * 손으로 적어 둔 251자 표보다 훨씬 넓다 (인명용 한자를 덮는다).
 */
export function readName(name: string, pillars: ChartPillars, hanjaReadings: Record<string, string> = {}, locale?: Locale): NameReading {
  const chartCounts = elementCounts(pillars)
  const { weakest: needed, strongest } = elementBalance(chartCounts)

  /*
   * 일본어 한자 이름은 **읽기(가나)로 바꿔 같은 음절 단위로** 나눈다 — 佐藤美咲 · さとう みさき · サトウ ミサキ 가
   * 같은 소리(さ·とう·み·さ·き)·같은 기운이 된다 (18 에서 값을 한 번만 받게 했는데 화면 숫자·결론이 달랐다).
   * 읽기를 못 받은 한자가 하나라도 있으면 예전처럼 글자마다.
   */
  const jaRead = locale === 'ja' && /\p{Script=Han}/u.test(name) && [...name].every((c) => !/\p{Script=Han}/u.test(c) || !!hanjaReadings[c] || c === '々')
    ? [...name].map((c, i, all) => (c === '々' ? hanjaReadings[all[i - 1] ?? ''] ?? '' : hanjaReadings[c] ?? c)).join('')
    : null
  const units: NameUnit[] = splitName(jaRead ?? name).map((text, i) => {
    if (LATIN_WORD.test(text)) return { text, element: latinElement(text, locale) }
    // 서버가 읽어 준 음이 있으면 그 음의 첫소리로 본다 (한국어면 한국 한자음)
    const said = hanjaReadings[text]
    /*
     * 그래도 못 읽으면 Unihan 표 — 화면 언어의 읽기로 (한국 한자음 · 병음 · 음독).
     * 전에 읽히던 글자는 앞의 두 방법에서 이미 정해지므로 기운이 바뀌지 않는다 (저장 캐시 열쇠 그대로).
     */
    const fromReading = said ? (soundElement(said) ?? latinElement(said, locale)) : null
    const nativeFirst = locale === 'ja' || locale === 'zh-Hans' || locale === 'zh-Hant'
    return { text, element: fromReading ?? (nativeFirst ? hanjaTableElement(text, hanjaSystem(locale), i === 0) ?? soundElement(text) : soundElement(text) ?? hanjaTableElement(text, hanjaSystem(locale), i === 0)) }
  })

  const counts = Object.fromEntries(ELEMENTS.map((e) => [e, 0])) as Record<Element, number>
  for (const u of units) if (u.element) counts[u.element] += 1

  const nameElements = ELEMENTS.filter((e) => counts[e] > 0)
  /*
   * **동점을 함께 본다.** 예전에는 가장 적은·많은 기운 하나(목·화·토·금·수 순서의 첫 것)만 보아,
   * en Eleanor(나무·물 1 동점, 이름이 물)가 "채움 없음", zh-Hant 林雅婷(木火土 2 동점, 이름 火 둘 → 火 4)가 "더함 無" 였다.
   *   채움  사주에서 가장 적은 기운(동점 모두) 가운데 이름이 가진 것 모두
   *   더함 이름을 더한 뒤 가장 많은 기운(동점 모두) 가운데 이름이 보탠 것 — **채운 기운도 넘쳐서 가장 많아지면 함께** (
   *         ja 佐々木美咲 金 사주1+이름3=4 가 土3 보다 많아졌는데 채운 기운이라 빼서 "더함 없음" 이었다)
   * 화면 칸·고정 문장·AI 재료(fit·fitLetters)가 모두 이 두 목록을 쓴다.
   */
  const chartBalance = elementBalance(chartCounts)
  const coverList = chartBalance.least.filter((e) => counts[e] > 0)
  const combinedMax = Math.max(...ELEMENTS.map((e) => chartCounts[e] + counts[e]))
  const pileList = ELEMENTS.filter((e) => counts[e] > 0 && chartCounts[e] + counts[e] === combinedMax)
  const covers = coverList.length > 0
  const piles = pileList[0] ?? null

  const pairs: NamePair[] = []
  for (let i = 0; i + 1 < units.length; i += 1) {
    const a = units[i]!
    const b = units[i + 1]!
    if (!a.element || !b.element) continue
    pairs.push({ from: a.text, to: b.text, bond: bondOf(a.element, b.element) })
  }

  // 가장 센 소리 — 같은 수면 이름에서 먼저 나오는 쪽
  let dominant: Element | null = null
  for (const u of units) {
    if (!u.element) continue
    if (dominant === null || counts[u.element] > counts[dominant]) dominant = u.element
  }

  const combined = Object.fromEntries(ELEMENTS.map((e) => [e, chartCounts[e] + counts[e]])) as Record<Element, number>

  /*
   * 별명 제안은 **이름을 보고 고른다.**
   * 예전에는 늘 `needed`(사주에서 모자란 것) 하나여서, 이름을 무엇으로 바꿔도
   * 같은 문장이 나왔다 (제보). 이름이 이미 그 기운을 담고 있으면 채울 이유가 없으니,
   * **합친 수에서 가장 모자라고 이름에 아직 없는** 기운을 고른다.
   */
  /*
   * **권하는 기운 = 사주에서 가장 모자란 기운(`needed`) 하나로 되돌린다.**
   * 위처럼 "이름에 아직 없는 기운" 으로 바꿔 고르자, 같은 화면에서 "사주는 불 火 쪽이 모자라고" 바로 아래
   * "내 사주에 모자란 나무 木 기운은 ㄱ·ㅋ…" 처럼 모자란 기운이 둘로 나왔다(문장은 "내 사주에 모자란" 이다).
   * 이름이 이미 그 소리를 가졌으면 갈래(suggestWhere·nickAdvice)가 "이미 있다" 쪽으로 간다.
   */
  const suggest = needed

  /*
   * **실제로 있는 관계만 말한다**.
   *
   * 예전에는 갈래가 둘 이상이면 곧바로 `mixed` 였고, 화면은 그때 "미는 자리와
   * 누르는 자리가 섞여 있어요" 를 찍었다. 그런데 정상훈은 **같은 기운 + 받쳐 주는**
   * 관계뿐이라 누르는 자리가 하나도 없다 — 없는 것을 있다고 말한 셈이다.
   * 이제 상생·상극이 **함께** 있을 때만 섞였다고 한다.
   */
  const hasFeed = pairs.some((p) => isFeed(p.bond))
  const hasPress = pairs.some((p) => isPress(p.bond))
  const hasSame = pairs.some((p) => p.bond === 'same')
  const bondMood: NameReading['bondMood'] =
    pairs.length === 0 ? 'none'
    : hasFeed && hasPress ? 'mixed'
    : hasFeed ? 'feeds'
    : hasPress ? 'presses'
    : hasSame ? 'same'
    : 'plain'

  // --- 뒷글을 가르려고 보는 값들 ---
  const readableUnits = units.filter((u) => u.element !== null)
  const lastElement = readableUnits[readableUnits.length - 1]?.element ?? null
  const first = readableUnits[0]?.element ?? null
  const endsMatch = first !== null && first === lastElement

  /*
   * 같은 기운이 섞여 있어도 상생만 있으면 "미는 자리와 누르는 자리가 섞였다" 가 아니다.
   * 그 경우를 따로 둔다 (`feedsSame`·`pressesSame`) — 정상훈이 여기다.
   */
  const flow: NameReading['flow'] =
    pairs.length === 0 ? 'none'
    : hasFeed && hasPress ? 'mixed'
    : hasFeed ? (hasSame ? 'feedsSame' : 'feeds')
    : hasPress ? (hasSame ? 'pressesSame' : 'presses')
    : hasSame ? 'steady'
    : 'mixed'

  let repeatElement: Element | null = null
  let repeatCount = 0
  for (const e of ELEMENTS) if (counts[e] > repeatCount) { repeatCount = counts[e]; repeatElement = e }
  const readableCount = readableUnits.length
  const repeat: NameReading['repeat'] =
    readableCount >= 2 && repeatCount === readableCount ? 'all' : repeatCount >= 2 ? 'some' : 'none'

  const length: NameReading['length'] = readableCount <= 1 ? 'short' : readableCount >= 4 ? 'long' : 'normal'

  const fit: NameReading['fit'] = covers && piles ? 'both' : covers ? 'covers' : piles ? 'piles' : 'none'

  /*
   * **어느 글자가 채우거나 얹는가.** 자리를 함께 보면 같은 `fit` 이라도 글이 갈린다 —
   * 정운영자(첫 글자부터)과 오유진(끝 글자만)은 둘 다 `piles` 지만 읽히는 모습이 다르다.
   */
  const fitElements: Element[] = fit === 'none' ? [] : [...new Set([...coverList, ...pileList])]
  const fitIndexes = readableUnits.map((u, i) => (u.element && fitElements.includes(u.element) ? i : -1)).filter((i) => i >= 0)
  const fitLetters = fitIndexes.map((i) => readableUnits[i]!.text)
  const coverLetters = readableUnits.filter((u): u is typeof u & { element: Element } => u.element !== null && coverList.includes(u.element)).map((u) => ({ text: u.text, element: u.element }))
  const pileLetters = readableUnits.filter((u): u is typeof u & { element: Element } => u.element !== null && pileList.includes(u.element)).map((u) => ({ text: u.text, element: u.element }))
  const lastIndex = readableUnits.length - 1
  const fitWhere: NameReading['fitWhere'] =
    fitIndexes.length === 0 ? 'none'
    // 모든 글자가 채우거나 보태도, 기운이 글자마다 다르면 "모든 글자가 그 기운이에요" 가 아니다 (같은 화면의 "글자마다 기운이 모두 달라요" 와 부딪혔다)
    : fitIndexes.length === readableUnits.length ? (new Set(readableUnits.map((u) => u.element)).size === 1 ? 'spread' : 'spreadMixed')
    : fitIndexes.includes(0) ? 'first'
    : fitIndexes.includes(lastIndex) ? 'last'
    : 'middle'

  /*
   * **별명을 권할 자리인가.** 예전에는 갈래 없이 한 문장이었다.
   * 이름이 이미 모자란 기운을 채우면 권할 것이 없고, 첫소리가 이미 넉넉한 기운이면
   * 부르는 소리부터 그쪽으로 기우는 셈이라 할 말이 다르다.
   */
  const nickAdvice: NameReading['nickAdvice'] =
    covers ? 'enough' : first === strongest ? 'fromStrong' : 'suggest'

  /*
   * **권하는 소리가 이름 안에 이미 있는가.** 있으면 그 글자를 앞세우거나
   * 애칭으로 부르는 쪽을 권할 수 있다 — 새 이름을 찾을 것도 없다.
   */
  const suggestIdx = readableUnits.map((u, i) => (u.element === suggest ? i : -1)).filter((i) => i >= 0)
  const suggestLetters = suggestIdx.map((i) => readableUnits[i]!.text)
  const suggestWhere: NameReading['suggestWhere'] =
    suggestIdx.length === 0 ? 'none'
    : suggestIdx.length === readableUnits.length ? 'spread'
    : suggestIdx.includes(0) ? 'first'
    : suggestIdx.includes(readableUnits.length - 1) ? 'last'
    : 'middle'

  /** 지금 첫소리가 권하는 기운에서 얼마나 먼가 */
  const suggestGap: NameReading['suggestGap'] =
    first === null ? 'near' : first === suggest ? 'same' : isPress(bondOf(first, suggest)) ? 'clashes' : 'near'

  return {
    units,
    nameElements,
    counts,
    chartCounts,
    needed,
    strongest,
    covers,
    piles,
    coverList,
    pileList,
    pairs,
    unreadable: units.filter((u) => u.element === null).length,
    suggest,
    dominant,
    soundTied: tiedTop(counts),
    chartTied: tiedTop(chartCounts),
    readable: units.filter((u) => u.element !== null).length,
    combined,
    fillsWith: coverList.reduce((n, e) => n + counts[e], 0),
    bondMood,
    fitWhere,
    fitLetters,
    coverLetters,
    pileLetters,
    nickAdvice,
    suggestWhere,
    suggestLetters,
    suggestGap,
    firstElement: first,
    lastElement,
    endsMatch,
    flow,
    repeat,
    repeatElement: repeatCount >= 2 ? repeatElement : null,
    repeatCount,
    length,
    fit,
  }
}

/** 그 오행의 첫소리 보기 (별명·영어 이름을 고를 때) */
export const SOUND_HINTS: Record<Element, { ko: string; latin: string }> = {
  wood: { ko: 'ㄱ, ㅋ', latin: 'G, K, Q' },
  fire: { ko: 'ㄴ, ㄷ, ㄹ, ㅌ', latin: 'N, D, L, T, R' },
  earth: { ko: 'ㅇ, ㅎ', latin: 'A, E, I, O, U, H' },
  metal: { ko: 'ㅅ, ㅈ, ㅊ', latin: 'S, J, C, Z' },
  water: { ko: 'ㅁ, ㅂ, ㅍ', latin: 'M, B, P, F' },
}

/**
 * **그 화면 사람이 읽는 꼴의 첫소리.** 일본어는 가나 행(か行), 중국어는 병음 첫소리(g、k),
 * 스페인어·포르투갈어는 그 언어에서 그 소리가 나는 철자 — es 의 Ge·Gi·J 는 ㅎ 소리라 나무·쇠에 넣지 않고,
 * Ca·Co·Cu 는 ㄱ 소리라 쇠에 넣지 않는다 (pt Ge·Gi·J 는 ㅈ 이라 쇠).
 */
const SOUND_HINTS_LOCAL: Partial<Record<string, Record<Element, string>>> = {
  ja: { wood: 'か行, が行', fire: 'な行, た行, だ行, ら行', earth: 'あ行, や行, わ行, は行', metal: 'さ行, ざ行, ち', water: 'ま行, ば行, ぱ行' },
  'zh-Hans': { wood: 'g, k', fire: 'n, d, l, t, r', earth: 'a, e, o, y, w, h', metal: 's, sh, z, zh, c, ch, j, q, x', water: 'm, b, p, f' },
  'zh-Hant': { wood: 'ㄍ(g), ㄎ(k)', fire: 'ㄋ(n), ㄉ(d), ㄌ(l), ㄊ(t), ㄖ(r)', earth: 'ㄚ(a), ㄜ(e), ㄛ(o), ㄧ(y/i), ㄨ(w/u), ㄏ(h)', metal: 'ㄙ(s), ㄕ(sh), ㄗ(z), ㄓ(zh), ㄘ(c), ㄔ(ch), ㄐ(j), ㄑ(q), ㄒ(x)', water: 'ㄇ(m), ㄅ(b), ㄆ(p), ㄈ(f)' },
  // latinElement 와 같은 표 (es Ge·Gi·J = ㅎ 흙, pt·en Ge·Gi·J = ㅈ 쇠, Ca·Co·Cu = ㄱ 나무)
  es: { wood: 'Ga, Go, Gu, Ca, Co, Cu, K, Qu', fire: 'N, D, L, T, R', earth: 'A, E, I, O, U, H, J, Ge, Gi', metal: 'S, Z, Ce, Ci, Ch', water: 'M, B, P, F' },
  'pt-BR': { wood: 'Ga, Go, Gu, Ca, Co, Cu, K, Qu', fire: 'N, D, L, T', earth: 'A, E, I, O, U, H, R', metal: 'S, J, Z, Ce, Ci, Ç, Ge, Gi, Ch', water: 'M, B, P, F' },
  // en 은 한국어 로마자처럼 읽히던 "Ga, Go, Gu, Ca, Co, Cu" 대신 영어 철자 보기 (같은 규칙 — 딱딱한 C·G 는 나무, 부드러운 C·G 는 쇠)
  en: { wood: 'K, Q, C (Cole), G (Gary)', fire: 'N, D, L, T, R', earth: 'A, E, I, O, U, H', metal: 'S, J, Z, Ch, C (Cindy), G (Gina)', water: 'M, B, P, F' },
}

export function soundHint(locale: string, element: Element): string {
  return SOUND_HINTS_LOCAL[locale]?.[element] ?? SOUND_HINTS[element].latin
}

/**
 * **사주에서 가장 모자란 기운** — 이름 풀이의 `needed` 와 같은 셈 (학교 첫소리 한 줄에 쓴다).
 */
export function neededElement(pillars: ChartPillars): Element {
  return elementBalance(elementCounts(pillars)).weakest
}

/** 그 기운의 한글 첫소리를 "ㅅ·ㅈ·ㅊ" 꼴로 (이름 풀이와 같은 표 — `SOUND_HINTS`) */
export function koSoundsOf(element: Element): string {
  return SOUND_HINTS[element].ko.replace(/, /g, '·')
}
